import {
  CaseSizeRuleListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  pluralize,
  getOrderItemCountOfContainerType,
  groupItemsByCaseSizeRules,
  getContainerTypes,
} from "@sellar-io/common";
import { BadgeIndigo } from "../../../../../../../components/badge/Badge";
import { ProgressBarIndigo } from "../../../../../../../components/progressBar/ProgressBar";
import { Icon } from "../../../../../../../components";

type OrderItem = {
  quantity: number;
  containerType: string | null;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
};

export function MinimumOrderQuantityProgress({
  orderItemList,
  rule,
  caseSizeRuleList,
  onClick,
}: {
  orderItemList: OrderItem[];
  rule: SupplierGetResponseType["CustomerZones"][number]["PriceTier"]["PriceTierOrderRules"][number];
  caseSizeRuleList: Pick<
    CaseSizeRuleListResponseType[number],
    "quantity" | "containerType" | "volume" | "unit"
  >[];
  onClick: () => void;
}) {
  const shouldCheckContainerSize = rule.unit && rule.volume;

  const isOrderRuleApplicable =
    !!orderItemList.find((item) => {
      const matchesContainerSizeOrAbove = shouldCheckContainerSize
        ? item.unit === rule.unit && (item.volume ?? 0) >= (rule.volume ?? 0)
        : true;
      return (
        getContainerTypes(item.containerType).includes(rule.itemType) &&
        matchesContainerSizeOrAbove
      );
    }) || !!(orderItemList.length && rule.itemType === "any");

  const orderItemsGroupedByCaseSizeRules = groupItemsByCaseSizeRules({
    caseSizeRuleList,
    orderItemList,
  });

  const quantityApplicableToOrderRule = orderItemsGroupedByCaseSizeRules.reduce(
    (total, item) => {
      const matchesContainerSizeOrAbove = shouldCheckContainerSize
        ? item.unit === rule.unit && (item.volume ?? 0) >= (rule.volume ?? 0)
        : true;

      if (item.containerType && matchesContainerSizeOrAbove) {
        return (
          total +
          Math.floor(
            getOrderItemCountOfContainerType({
              containerType: rule.itemType,
              orderItemList: [item],
            }),
          )
        );
      }
      return total;
    },
    0,
  );

  // if order rule is applicable, minimum percentageProgress is 1
  const percentageComplete = isOrderRuleApplicable
    ? Math.max(
        Math.floor((quantityApplicableToOrderRule / rule.quantity) * 100),
        1,
      )
    : 0;

  const isOrderRuleMet = quantityApplicableToOrderRule >= rule.quantity;

  const generateProgressText = () => {
    const amountLeft = rule.quantity - quantityApplicableToOrderRule;

    if (rule.itemType === "any") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {pluralize("item", amountLeft)}. Minimum of {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "can") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {`${pluralize("case", amountLeft)} of cans`}. Minimum of{" "}
          {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "bottle") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {`${pluralize("case", amountLeft)} of bottles`}. Minimum of{" "}
          {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "draught") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>{" "}
          {pluralize("draught item", amountLeft)}. Minimum of {rule.quantity}{" "}
          per order
        </div>
      );
    }

    return (
      <div className="text-indigo-800">
        Add <span className="font-bold">{amountLeft} more </span>{" "}
        {pluralize(rule.itemType, amountLeft)}
        {rule.unit && rule.volume ? ` over ${rule.volume}${rule.unit}` : ""}.
        Minimum of {rule.quantity} per order
      </div>
    );
  };

  if (isOrderRuleMet || !isOrderRuleApplicable) {
    return null;
  }

  return (
    <div
      className="grid cursor-pointer grid-cols-5 items-center gap-2 rounded-lg border border-solid border-indigo-300 bg-indigo-100 p-2"
      onClick={onClick}
    >
      <div className="col-span-5 flex items-center gap-2 md:col-span-3">
        <BadgeIndigo icon={"CLIPBOARD_CHECK_OUTLINE"} />
        <div>{generateProgressText()}</div>
      </div>

      <div className="col-span-5 flex items-center gap-2 md:col-span-2">
        <ProgressBarIndigo percentage={percentageComplete} />
        <Icon icon={"CHEVRON_RIGHT"} size={1} />
      </div>
    </div>
  );
}
